import type { KarmaBirthDate, KarmaCircleRule, KarmaCircleRuleKey, KarmaCircleRuleSet } from "@/lib/karma/types";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { getSessionUser } from "@/lib/supabase/auth";
import { getSupabaseClient } from "@/lib/supabase/client";
import { upsertMyProfile } from "@/lib/supabase/profile";

export type CircleSummary = {
  id: string;
  name: string;
  ownerId: string | null;
  createdAt: string | null;
  membersCount: number;
  rules: KarmaCircleRuleSet | null;
  activity: string | null;
  description: string | null;
  imageUrl: string | null;
};

export type CircleMemberRole = "member" | "admin";

export type CircleMemberProfile = {
  id: string;
  displayName: string;
  handle: string;
  photoUrl: string | null;
  role: CircleMemberRole;
};

export type CircleTaskKind = "meditasyon" | "nefes" | "diyet" | "rituel" | "sohbet" | "ozel";

export type CircleTask = {
  id: string;
  circleId: string;
  title: string;
  description: string | null;
  kind: CircleTaskKind;
  points: number;
  sortOrder: number;
  active: boolean;
  createdAt: string | null;
  createdBy: string | null;
};

export type CircleTaskWithStatus = CircleTask & {
  completed: boolean;
  dateKey: string;
};

export type CircleMessage = {
  id: string;
  circleId: string;
  userId: string;
  body: string;
  createdAt: string;
  senderName: string;
};

export type CircleInviteStatus = "pending" | "accepted" | "declined" | "expired";

export type CircleInvite = {
  id: string;
  circleId: string;
  circleName: string;
  senderUserId: string;
  senderName: string;
  receiverUserId: string;
  inviteType: "manual" | "numerology_match";
  matchScore: number | null;
  message: string | null;
  status: CircleInviteStatus;
  createdAt: string;
  respondedAt: string | null;
};

export type CircleDraftData = {
  id: string;
  name: string;
  description: string;
  activity: string;
  imageUrl: string;
  circleType: string;
  durationDays: number | null;
  maxParticipants: number | null;
  socialFeatures: Record<string, any> | null;
  tasks: Array<Record<string, any>>;
  rules: KarmaCircleRuleSet | null;
  status: "draft" | "active" | "completed" | "archived";
  updatedAt: string | null;
};

export type CircleNumerologyMatchCandidate = {
  userId: string;
  displayName: string;
  handle: string;
  photoUrl: string | null;
  matchScore: number;
  matchedRules: number;
  totalRules: number;
};

function makeUuidV4Fallback() {
  // RFC4122-ish v4 fallback (used only if crypto.randomUUID is unavailable)
  const hex = "0123456789abcdef";
  const rnd = (n: number) => {
    let out = "";
    for (let i = 0; i < n; i++) out += hex[Math.floor(Math.random() * 16)];
    return out;
  };

  return `${rnd(8)}-${rnd(4)}-4${rnd(3)}-${hex[8 + Math.floor(Math.random() * 4)]}${rnd(3)}-${rnd(12)}`;
}

function makeCircleId() {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid;
  } catch {
    // ignore
  }

  return makeUuidV4Fallback();
}

function parseBirthDateFromIso(value: unknown): KarmaBirthDate | null {
  if (typeof value !== "string" || !value) return null;
  const [y, m, d] = value.split("-").map((v) => Number(v));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (y <= 0 || m <= 0 || d <= 0) return null;
  return { year: Math.trunc(y), month: Math.trunc(m), day: Math.trunc(d) };
}

function formatDisplayNameFromHandle(value: unknown) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toLocaleUpperCase("tr-TR"));
}

function isGenericDisplayName(value: unknown) {
  const normalized = String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return true;
  if (["kullanıcı", "kullanici", "user", "biri", "üye", "uye"].includes(normalized)) return true;
  return normalized === "bir fincan sevgi" || normalized.startsWith("bir fincan sevgi ");
}

function normalizeProfileName(row: any) {
  if (!row) return "Biri";

  const displayName = typeof row.display_name === "string" ? row.display_name.trim() : "";
  if (!isGenericDisplayName(displayName)) return displayName;

  const handle = typeof row.handle === "string" ? row.handle.trim() : "";
  const normalizedHandle = formatDisplayNameFromHandle(handle);
  if (normalizedHandle) return normalizedHandle;

  return displayName || "Biri";
}

function normalizeRuleSet(raw: unknown): KarmaCircleRuleSet | null {
  if (!raw || typeof raw !== "object") return null;
  const anyRaw = raw as any;
  const allRaw = anyRaw?.all;
  if (!Array.isArray(allRaw)) return null;

  const allowedKeys: KarmaCircleRuleKey[] = [
    "destinyNumber",
    "expressionNumber",
    "heartDesireNumber",
    "personalityNumber",
    "birthDayNumber",
    "karmicDebt",
  ];

  const all: KarmaCircleRule[] = allRaw
    .map((it: any) => {
      const key = typeof it?.key === "string" ? (it.key as KarmaCircleRuleKey) : null;
      const value = Math.trunc(Number(it?.value));
      if (!key || !allowedKeys.includes(key)) return null;
      if (!Number.isFinite(value)) return null;
      return { key, value } satisfies KarmaCircleRule;
    })
    .filter(Boolean) as KarmaCircleRule[];

  return { all };
}

function isRestricted(rules: KarmaCircleRuleSet | null) {
  return Boolean(rules?.all?.length);
}

function userMatchesCircleRules(args: { fullName: string; birthDate: KarmaBirthDate; rules: KarmaCircleRuleSet }) {
  const rules = args.rules?.all ?? [];
  if (rules.length === 0) return true;

  const nu = computeKarmaNumerology(args.fullName, args.birthDate);

  const karmicDebtExpected = rules
    .filter((r) => r.key === "karmicDebt")
    .map((r) => Math.trunc(r.value))
    .filter((n) => Number.isFinite(n));

  if (karmicDebtExpected.length) {
    const ok = karmicDebtExpected.some((n) => nu.karmicCodes.includes(n));
    if (!ok) return false;
  }

  for (const rule of rules) {
    if (rule.key === "karmicDebt") continue;

    const expected = Math.trunc(rule.value);
    const actual =
      rule.key === "destinyNumber"
        ? nu.destinyNumber
        : rule.key === "expressionNumber"
          ? nu.expressionNumber
          : rule.key === "heartDesireNumber"
            ? nu.heartDesireNumber
            : rule.key === "personalityNumber"
              ? nu.personalityNumber
              : nu.birthDayNumber;

    if (actual !== expected) return false;
  }

  return true;
}

function computeCircleRuleMatchScore(args: { fullName: string; birthDate: KarmaBirthDate; rules: KarmaCircleRuleSet }) {
  const rules = args.rules?.all ?? [];
  if (!rules.length) return { score: 0, matched: 0, total: 0 };

  const nu = computeKarmaNumerology(args.fullName, args.birthDate);
  let matched = 0;

  for (const rule of rules) {
    const expected = Math.trunc(rule.value);
    if (!Number.isFinite(expected)) continue;

    if (rule.key === "karmicDebt") {
      if (nu.karmicCodes.includes(expected)) matched += 1;
      continue;
    }

    const actual =
      rule.key === "destinyNumber"
        ? nu.destinyNumber
        : rule.key === "expressionNumber"
          ? nu.expressionNumber
          : rule.key === "heartDesireNumber"
            ? nu.heartDesireNumber
            : rule.key === "personalityNumber"
              ? nu.personalityNumber
              : nu.birthDayNumber;

    if (actual === expected) matched += 1;
  }

  const total = rules.length;
  const score = total > 0 ? Math.round((matched / total) * 100) : 0;
  return { score: Math.max(0, Math.min(100, score)), matched, total };
}

async function fetchMyProfileForRules(userId: string) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("karma_profiles")
    .select("id, display_name, birth_date")
    .eq("id", userId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const displayName = typeof (data as any).display_name === "string" ? String((data as any).display_name) : "";
  const birthDate = parseBirthDateFromIso((data as any).birth_date);

  return {
    fullName: displayName.trim(),
    birthDate,
  };
}

async function fetchMemberCounts(circleIds: string[]) {
  const ids = Array.from(new Set(circleIds.filter(Boolean)));
  const out: Record<string, number> = {};
  if (!ids.length) return out;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("circle_members").select("circle_id").in("circle_id", ids);
  if (error) throw error;

  for (const row of (data ?? []) as any[]) {
    const id = String(row.circle_id ?? "");
    if (!id) continue;
    out[id] = (out[id] ?? 0) + 1;
  }

  return out;
}

async function fetchProfilesByIds(userIds: string[]) {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  const out = new Map<string, any>();
  if (!ids.length) return out;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("karma_profiles").select("id, display_name, handle, photo_url").in("id", ids);
  if (error) throw error;

  for (const row of (data ?? []) as any[]) {
    const id = String(row.id ?? "");
    if (!id) continue;
    out.set(id, row);
  }

  return out;
}

function normalizeCircleRow(row: any, membersCount: number): CircleSummary {
  const rawRules = row?.rules;
  const imageFromRules = typeof rawRules?.image_url === "string" ? rawRules.image_url : typeof rawRules?.imageUrl === "string" ? rawRules.imageUrl : null;
  const imageFromColumn = typeof row?.image_url === "string" ? row.image_url : null;

  return {
    id: String(row?.id ?? ""),
    name: String(row?.name ?? ""),
    ownerId: row?.owner_id ? String(row.owner_id) : null,
    createdAt: row?.created_at ? String(row.created_at) : null,
    membersCount,
    rules: normalizeRuleSet(rawRules),
    activity: typeof row?.activity === "string" ? row.activity : null,
    description: typeof row?.description === "string" ? row.description : null,
    imageUrl: imageFromColumn || imageFromRules,
  };
}

function mapRuleKeyToFilterType(key: KarmaCircleRuleKey) {
  if (key === "destinyNumber") return "life_path";
  if (key === "heartDesireNumber") return "heart_desire";
  if (key === "expressionNumber") return "expression_number";
  if (key === "personalityNumber") return "personality_number";
  if (key === "karmicDebt") return "karmic_debt";
  if (key === "birthDayNumber") return "birth_day";
  return String(key);
}

function mapActivityToNormalizedType(activity: string) {
  const value = String(activity ?? "").trim().toLocaleLowerCase("tr-TR");
  if (value === "meditasyon") return "meditation";
  if (value === "nefes") return "breath";
  if (value === "rituel" || value === "ritüel") return "ritual";
  if (value === "diyet") return "diet";
  if (value === "sohbet") return "chat";
  return value;
}

function parseActivityList(source: { activities?: string[]; activity?: string }) {
  if (Array.isArray(source.activities) && source.activities.length) {
    return source.activities.map((value) => String(value).trim()).filter(Boolean);
  }

  return String(source.activity || "")
    .split("|")
    .slice(-1)
    .join("")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function isMissingRelationError(error: any) {
  const code = String(error?.code ?? "");
  const message = String(error?.message ?? "").toLocaleLowerCase("tr-TR");
  return code === "42P01" || code === "42703" || message.includes("could not find") || message.includes("column") || message.includes("relation");
}

async function syncCircleFiltersInDb(args: { circleId: string; rules: KarmaCircleRuleSet | null }) {
  if (!args.rules?.all?.length) return;
  const supabase = getSupabaseClient();

  const filterRows = args.rules.all
    .map((rule) => {
      const value = String(Math.trunc(Number(rule.value)));
      if (!value) return null;
      return {
        circle_id: args.circleId,
        filter_type: mapRuleKeyToFilterType(rule.key),
        filter_value: value,
      };
    })
    .filter(Boolean) as Array<{ circle_id: string; filter_type: string; filter_value: string }>;

  try {
    const { error: deleteError } = await supabase.from("circle_filters").delete().eq("circle_id", args.circleId);
    if (deleteError) throw deleteError;
    if (!filterRows.length) return;
    const { error: insertError } = await supabase.from("circle_filters").insert(filterRows as any);
    if (insertError) throw insertError;
  } catch (error) {
    if (!isMissingRelationError(error)) throw error;
  }
}

async function syncCircleActivitiesInDb(args: { circleId: string; activities: string[] }) {
  const values = Array.from(new Set((args.activities ?? []).map((item) => mapActivityToNormalizedType(item)).filter(Boolean)));
  if (!values.length) return;

  const supabase = getSupabaseClient();
  const activityRows = values.map((activity, index) => ({
    circle_id: args.circleId,
    activity_type: activity,
    is_active: true,
    sort_order: index,
  }));

  try {
    const { error: deleteError } = await supabase.from("circle_activities").delete().eq("circle_id", args.circleId);
    if (deleteError) throw deleteError;

    const { error: insertError } = await supabase.from("circle_activities").insert(activityRows as any);
    if (insertError) throw insertError;
  } catch (error) {
    if (!isMissingRelationError(error)) throw error;
  }
}

export async function estimateCircleMatchCount(rulesInput: KarmaCircleRuleSet) {
  const rules = normalizeRuleSet(rulesInput);
  if (!rules?.all?.length) return { count: 0, scanned: 0 };

  const supabase = getSupabaseClient();
  const pageSize = 1000;
  const maxScan = 10_000;

  let from = 0;
  let count = 0;
  let scanned = 0;

  while (scanned < maxScan) {
    const { data, error } = await supabase
      .from("karma_profiles")
      .select("display_name, birth_date")
      .not("birth_date", "is", null)
      .range(from, from + pageSize - 1);

    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) break;

    for (const row of rows) {
      const fullName = String((row as any)?.display_name ?? "").trim();
      const birthDate = parseBirthDateFromIso((row as any)?.birth_date);
      if (!fullName || !birthDate) continue;
      scanned += 1;
      if (userMatchesCircleRules({ fullName, birthDate, rules })) {
        count += 1;
      }
      if (scanned >= maxScan) break;
    }

    if (rows.length < pageSize) break;
    from += pageSize;
  }

  return { count, scanned };
}

export async function createCircleInDb(args: {
  name: string;
  rules: KarmaCircleRuleSet;
  description?: string;
  activity?: string;
  imageUrl?: string;
  activities?: string[];
  circleType?: string;
  status?: "draft" | "active" | "completed" | "archived";
  showOnRitualMap?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  meta?: {
    durationDays?: number | null;
    maxParticipants?: number | null;
    socialFeatures?: {
      energySystemEnabled?: boolean;
      streakEnabled?: boolean;
      leaderboardEnabled?: boolean;
      ritualHour?: string | null;
      aura?: { type: string; label: string; hz: number } | null;
      sharingAreaEnabled?: boolean;
      dailyEnergyEnabled?: boolean;
      badgeSystemEnabled?: boolean;
      matchScoreEnabled?: boolean;
      evolutionEnabled?: boolean;
      autoSuggestionEnabled?: boolean;
    } | null;
  };
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const name = args.name.trim().slice(0, 40);
  if (name.length < 2) throw new Error("Çember adı çok kısa");

  const rules = args.rules?.all?.length ? args.rules : null;
  if (!rules) throw new Error("En az 1 numerolojik filtre seçmelisin");

  const description = typeof args.description === "string" ? args.description.trim().slice(0, 280) : "";
  const activity = typeof args.activity === "string" ? args.activity.trim().slice(0, 24) : "";
  const imageUrl = typeof args.imageUrl === "string" ? args.imageUrl.trim().slice(0, 10_000) : "";
  const durationDaysRaw = Number(args.meta?.durationDays);
  const durationDays = Number.isFinite(durationDaysRaw) && durationDaysRaw > 0 ? Math.trunc(durationDaysRaw) : null;
  const maxParticipantsRaw = Number(args.meta?.maxParticipants);
  const maxParticipants = Number.isFinite(maxParticipantsRaw) && maxParticipantsRaw > 0 ? Math.trunc(maxParticipantsRaw) : null;
  const socialFeatures = args.meta?.socialFeatures && typeof args.meta.socialFeatures === "object" ? args.meta.socialFeatures : null;
  const rulesPayload = {
    ...rules,
    ...(imageUrl ? { image_url: imageUrl } : {}),
    ...(durationDays ? { duration_days: durationDays } : {}),
    ...(maxParticipants ? { max_participants: maxParticipants } : {}),
    ...(socialFeatures ? { social_features: socialFeatures } : {}),
  } as any;

  const durationType = durationDays === 7 ? "7_days" : durationDays === 21 ? "21_days" : durationDays === 40 ? "40_days" : "unlimited";
  const status = args.status ?? "active";
  const startsAt = args.startsAt ? new Date(args.startsAt).toISOString() : null;
  const endsAt = args.endsAt ? new Date(args.endsAt).toISOString() : null;

  const supabase = getSupabaseClient();

  // Prevent creating two circles with the same name.
  const existing = await supabase.from("circles").select("id").eq("name", name).maybeSingle();
  if (existing.error) throw existing.error;
  if (existing.data?.id) {
    throw new Error("Bu isimde bir çember zaten var. Lütfen farklı bir isim seçin.");
  }

  // Idempotent insert: we generate the id client-side so accidental retries can't create duplicates.
  const circleId = makeCircleId();

  const baseInsertPayload = {
    id: circleId,
    name,
    owner_id: sessionUser.id,
    rules: rulesPayload,
    description: description || null,
    activity: activity || null,
  } as any;

  const extendedInsertPayload = {
    ...baseInsertPayload,
    owner_user_id: sessionUser.id,
    title: name,
    circle_type: args.circleType ? String(args.circleType).slice(0, 80) : null,
    duration_type: durationType,
    max_members: maxParticipants,
    status,
    show_on_ritual_map: Boolean(args.showOnRitualMap),
    starts_at: startsAt,
    ends_at: endsAt,
    updated_at: new Date().toISOString(),
  } as any;

  let insertAttempt = await supabase
    .from("circles")
    .insert(extendedInsertPayload)
    .select("id, name, owner_id, created_at, rules, activity, description")
    .maybeSingle();

  if (insertAttempt.error && isMissingRelationError(insertAttempt.error)) {
    insertAttempt = await supabase
      .from("circles")
      .insert(baseInsertPayload)
      .select("id, name, owner_id, created_at, rules, activity, description")
      .maybeSingle();
  }

  let circleRow = insertAttempt.data;
  const insertError = insertAttempt.error as any;

  // If the request is retried and the row already exists, load it.
  if (!circleRow && insertError?.code === "23505") {
    const { data, error } = await supabase
      .from("circles")
      .select("id, name, owner_id, created_at, rules, activity, description")
      .eq("id", circleId)
      .maybeSingle();
    if (error) throw error;
    circleRow = data;
  }

  if (insertError && !circleRow) {
    // If you add a DB-level unique constraint on circles.name, surface a clearer message.
    if (insertError?.code === "23505") {
      throw new Error("Bu isimde bir çember zaten var. Lütfen farklı bir isim seçin.");
    }
    throw insertError;
  }
  if (!circleRow) throw new Error("Çember oluşturulamadı");

  await syncCircleFiltersInDb({ circleId: String((circleRow as any).id), rules });

  const activityList = parseActivityList({ activities: args.activities, activity });

  await syncCircleActivitiesInDb({ circleId: String((circleRow as any).id), activities: activityList });

  await upsertMyProfile({ displayName: sessionUser.email ?? "Kullanıcı", handle: "" });

  const { error: memberError } = await supabase
    .from("circle_members")
    .upsert({ circle_id: String((circleRow as any).id), user_id: sessionUser.id } as any, {
      onConflict: "circle_id,user_id",
      ignoreDuplicates: true,
    });

  if (memberError) throw memberError;

  return normalizeCircleRow(circleRow, 1);
}

export async function saveCircleDraftInDb(args: {
  draftId?: string | null;
  name?: string;
  description?: string;
  activity?: string;
  activities?: string[];
  imageUrl?: string;
  circleType?: string;
  durationDays?: number | null;
  maxParticipants?: number | null;
  socialFeatures?: Record<string, any> | null;
  tasks?: Array<Record<string, any>>;
  startsAt?: string | null;
  endsAt?: string | null;
  showOnRitualMap?: boolean;
  rules?: KarmaCircleRuleSet | null;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const durationDaysRaw = Number(args.durationDays);
  const durationDays = Number.isFinite(durationDaysRaw) && durationDaysRaw > 0 ? Math.trunc(durationDaysRaw) : null;
  const maxParticipantsRaw = Number(args.maxParticipants);
  const maxParticipants = Number.isFinite(maxParticipantsRaw) && maxParticipantsRaw > 0 ? Math.trunc(maxParticipantsRaw) : null;
  const durationType = durationDays === 7 ? "7_days" : durationDays === 21 ? "21_days" : durationDays === 40 ? "40_days" : "unlimited";

  const name = String(args.name ?? "").trim().slice(0, 40) || "Taslak Çember";
  const description = String(args.description ?? "").trim().slice(0, 280);
  const activity = String(args.activity ?? "").trim().slice(0, 280);
  const imageUrl = String(args.imageUrl ?? "").trim().slice(0, 10_000);
  const circleType = String(args.circleType ?? "").trim().slice(0, 80);
  const rules = args.rules?.all?.length ? args.rules : ({ all: [] } as KarmaCircleRuleSet);
  const socialFeatures = args.socialFeatures && typeof args.socialFeatures === "object" ? args.socialFeatures : null;
  const tasks = Array.isArray(args.tasks) ? args.tasks : [];

  const rulesPayload = {
    ...rules,
    ...(imageUrl ? { image_url: imageUrl } : {}),
    ...(durationDays ? { duration_days: durationDays } : {}),
    ...(maxParticipants ? { max_participants: maxParticipants } : {}),
    ...(socialFeatures ? { social_features: socialFeatures } : {}),
    draft_payload: {
      tasks,
      activity,
      circle_type: circleType,
      updated_at: new Date().toISOString(),
    },
  } as any;

  const patch = {
    name,
    description: description || null,
    activity: activity || null,
    rules: rulesPayload,
    owner_user_id: sessionUser.id,
    title: name,
    circle_type: circleType || null,
    duration_type: durationType,
    max_members: maxParticipants,
    status: "draft",
    show_on_ritual_map: Boolean(args.showOnRitualMap),
    starts_at: args.startsAt ? new Date(args.startsAt).toISOString() : null,
    ends_at: args.endsAt ? new Date(args.endsAt).toISOString() : null,
    updated_at: new Date().toISOString(),
  } as any;

  let draftId = args.draftId ? String(args.draftId) : "";
  if (draftId) {
    const { error } = await supabase.from("circles").update(patch).eq("id", draftId).eq("owner_id", sessionUser.id);
    if (error) throw error;
  } else {
    const createdId = makeCircleId();
    const insertPayload = { ...patch, id: createdId, owner_id: sessionUser.id } as any;
    const { error } = await supabase.from("circles").insert(insertPayload);
    if (error) throw error;
    draftId = createdId;
  }

  await syncCircleFiltersInDb({ circleId: draftId, rules });
  await syncCircleActivitiesInDb({ circleId: draftId, activities: parseActivityList({ activities: args.activities, activity }) });
  return { id: draftId };
}

function normalizeCircleDraftFromRow(raw: any, fallbackTasks: Array<Record<string, any>> = []): CircleDraftData {
  const rules = raw?.rules && typeof raw.rules === "object" ? raw.rules : {};
  const draftPayload = rules?.draft_payload && typeof rules.draft_payload === "object" ? rules.draft_payload : {};

  return {
    id: String(raw?.id ?? ""),
    name: String(raw?.name ?? ""),
    description: String(raw?.description ?? ""),
    activity: String(raw?.activity ?? draftPayload.activity ?? ""),
    imageUrl: typeof rules.image_url === "string" ? rules.image_url : "",
    circleType: String(draftPayload.circle_type ?? ""),
    durationDays: Number.isFinite(Number(rules.duration_days)) ? Math.trunc(Number(rules.duration_days)) : null,
    maxParticipants: Number.isFinite(Number(rules.max_participants)) ? Math.trunc(Number(rules.max_participants)) : null,
    socialFeatures: rules.social_features && typeof rules.social_features === "object" ? rules.social_features : null,
    tasks: Array.isArray(draftPayload.tasks) ? (draftPayload.tasks as Array<Record<string, any>>) : fallbackTasks,
    rules: normalizeRuleSet(rules),
    status: String(raw?.status ?? "draft") as CircleDraftData["status"],
    updatedAt: raw?.updated_at ? String(raw.updated_at) : null,
  };
}

export async function getMyLatestCircleDraftInDb(): Promise<CircleDraftData | null> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circles")
    .select("id, name, description, activity, rules, status, updated_at")
    .eq("owner_id", sessionUser.id)
    .eq("status", "draft")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && isMissingRelationError(error)) {
    return null;
  }
  if (error) throw error;
  if (!data) return null;

  return normalizeCircleDraftFromRow(data as any);
}

export async function getMyCircleDraftByIdInDb(circleId: string): Promise<CircleDraftData | null> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const id = String(circleId ?? "").trim();
  if (!id) return null;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circles")
    .select("id, name, description, activity, rules, status, updated_at")
    .eq("id", id)
    .eq("owner_id", sessionUser.id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const tasks = await listCircleTasks(id).catch(() => [] as CircleTask[]);
  const fallbackTasks = tasks.map((task) => ({
    id: task.id,
    title: task.title,
    description: task.description ?? "",
    kind: task.kind,
    points: task.points,
    frequency: "daily",
    durationMinutes: 10,
    active: task.active !== false,
    imageUrl: "",
  }));

  return normalizeCircleDraftFromRow(data as any, fallbackTasks);
}

export async function publishCircleDraftInDb(args: {
  circleId: string;
  status?: "active" | "completed" | "archived";
  name: string;
  description?: string;
  activity?: string;
  activities?: string[];
  imageUrl?: string;
  circleType?: string;
  durationDays?: number | null;
  maxParticipants?: number | null;
  socialFeatures?: Record<string, any> | null;
  startsAt?: string | null;
  endsAt?: string | null;
  showOnRitualMap?: boolean;
  rules: KarmaCircleRuleSet;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const durationDaysRaw = Number(args.durationDays);
  const durationDays = Number.isFinite(durationDaysRaw) && durationDaysRaw > 0 ? Math.trunc(durationDaysRaw) : null;
  const maxParticipantsRaw = Number(args.maxParticipants);
  const maxParticipants = Number.isFinite(maxParticipantsRaw) && maxParticipantsRaw > 0 ? Math.trunc(maxParticipantsRaw) : null;

  const rulesPayload = {
    ...args.rules,
    ...(args.imageUrl ? { image_url: String(args.imageUrl).trim().slice(0, 10_000) } : {}),
    ...(durationDays ? { duration_days: durationDays } : {}),
    ...(maxParticipants ? { max_participants: maxParticipants } : {}),
    ...(args.socialFeatures && typeof args.socialFeatures === "object" ? { social_features: args.socialFeatures } : {}),
  } as any;

  const durationType = durationDays === 7 ? "7_days" : durationDays === 21 ? "21_days" : durationDays === 40 ? "40_days" : "unlimited";

  const patch = {
    name: String(args.name).trim().slice(0, 40),
    description: args.description ? String(args.description).trim().slice(0, 280) : null,
    activity: args.activity ? String(args.activity).trim().slice(0, 280) : null,
    rules: rulesPayload,
    title: String(args.name).trim().slice(0, 40),
    circle_type: args.circleType ? String(args.circleType).slice(0, 80) : null,
    duration_type: durationType,
    max_members: maxParticipants,
    status: args.status ?? "active",
    show_on_ritual_map: Boolean(args.showOnRitualMap),
    starts_at: args.startsAt ? new Date(args.startsAt).toISOString() : null,
    ends_at: args.endsAt ? new Date(args.endsAt).toISOString() : null,
    updated_at: new Date().toISOString(),
  } as any;

  const { data, error } = await supabase
    .from("circles")
    .update(patch)
    .eq("id", args.circleId)
    .eq("owner_id", sessionUser.id)
    .select("id, name, owner_id, created_at, rules, activity, description")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("Taslak bulunamadı");

  await syncCircleFiltersInDb({ circleId: args.circleId, rules: args.rules });
  await syncCircleActivitiesInDb({ circleId: args.circleId, activities: parseActivityList({ activities: args.activities, activity: args.activity }) });

  const { error: memberError } = await supabase
    .from("circle_members")
    .upsert({ circle_id: args.circleId, user_id: sessionUser.id } as any, {
      onConflict: "circle_id,user_id",
      ignoreDuplicates: true,
    });

  if (memberError) throw memberError;

  return normalizeCircleRow(data, 1);
}

export async function joinCircleInDb(circleId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();

  const { data: circleRow, error: circleError } = await supabase
    .from("circles")
    .select("id, rules")
    .eq("id", circleId)
    .maybeSingle();

  if (circleError) throw circleError;
  if (!circleRow) throw new Error("Çember bulunamadı");

  const rules = normalizeRuleSet((circleRow as any).rules);
  if (rules?.all?.length) {
    const profile = await fetchMyProfileForRules(sessionUser.id);
    if (!profile?.fullName || !profile.birthDate) {
      throw new Error("Bu çembere katılmak için profilinde ad soyad ve doğum tarihi gerekli.");
    }

    const ok = userMatchesCircleRules({ fullName: profile.fullName, birthDate: profile.birthDate, rules });
    if (!ok) throw new Error("Bu çemberin filtrelerine uymuyorsun.");
  } else {
    // No rules => not allowed (prevents random circles)
    throw new Error("Bu çemberde filtre tanımlı değil. Lütfen başka bir çember seçin.");
  }

  await upsertMyProfile({ displayName: sessionUser.email ?? "Kullanıcı", handle: "" });

  const { error } = await supabase
    .from("circle_members")
    .upsert({ circle_id: circleId, user_id: sessionUser.id } as any, {
      onConflict: "circle_id,user_id",
      ignoreDuplicates: true,
    });

  if (error) throw error;
}

export async function leaveCircleInDb(circleId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { error } = await supabase.from("circle_members").delete().eq("circle_id", circleId).eq("user_id", sessionUser.id);
  if (error) throw error;
}

export async function listMyCircles(userId: string) {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("circle_members")
    .select("circle_id, circles(id, name, owner_id, created_at, rules, activity, description)")
    .eq("user_id", userId);

  if (error) throw error;

  const rows = (data ?? []) as any[];
  const circles = rows
    .map((r) => r.circles)
    .filter(Boolean)
    .map((c) => ({
      id: String(c.id),
      name: String(c.name ?? ""),
      owner_id: c.owner_id ? String(c.owner_id) : null,
      created_at: c.created_at ? String(c.created_at) : null,
      rules: c.rules ?? null,
      activity: typeof c.activity === "string" ? c.activity : null,
      description: typeof c.description === "string" ? c.description : null,
    }));

  const unique = new Map<string, any>();
  for (const c of circles) {
    if (!c.id) continue;
    if (!unique.has(c.id)) unique.set(c.id, c);
  }

  const list = Array.from(unique.values());
  const counts = await fetchMemberCounts(list.map((c) => c.id));

  return list
    .map((c) => normalizeCircleRow(c, counts[c.id] ?? 1))
    .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")));
}

export async function listDiscoverCircles(args: { userId: string; limit?: number }) {
  const supabase = getSupabaseClient();
  const limit = Math.max(1, Math.min(50, Math.trunc(args.limit ?? 12)));

  let discoverAttempt = await supabase
    .from("circles")
    .select("id, name, owner_id, created_at, rules, activity, description")
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (discoverAttempt.error && isMissingRelationError(discoverAttempt.error)) {
    discoverAttempt = await supabase
      .from("circles")
      .select("id, name, owner_id, created_at, rules, activity, description")
      .order("created_at", { ascending: false })
      .limit(limit);
  }

  const circles = discoverAttempt.data;
  const error = discoverAttempt.error;
  if (error) throw error;

  const circleRows = (circles ?? []) as any[];
  const ids = circleRows.map((c) => String(c.id ?? "")).filter(Boolean);
  const counts = await fetchMemberCounts(ids);

  const { data: memberships, error: memberError } = await supabase
    .from("circle_members")
    .select("circle_id")
    .eq("user_id", args.userId)
    .in("circle_id", ids);

  if (memberError) throw memberError;

  const joined = new Set((memberships ?? []).map((r: any) => String(r.circle_id ?? "")).filter(Boolean));

  const profile = await fetchMyProfileForRules(args.userId);
  if (!profile?.fullName || !profile.birthDate) {
    throw new Error("Önerilen çemberler için profilinde ad soyad ve doğum tarihi gerekli.");
  }

  const fullName = profile.fullName;
  const birthDate = profile.birthDate;

  return circleRows
    .map((c) => normalizeCircleRow(c, counts[String(c.id ?? "")] ?? 0))
    .filter((c) => !joined.has(c.id))
    .filter((c) => isRestricted(c.rules))
    .filter((c) => (c.rules ? userMatchesCircleRules({ fullName, birthDate, rules: c.rules }) : false));
}

export async function fetchCircleById(circleId: string) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circles")
    .select("id, name, owner_id, created_at, rules, activity, description")
    .eq("id", circleId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const counts = await fetchMemberCounts([String((data as any).id)]);
  return normalizeCircleRow(data, counts[String((data as any).id)] ?? 0);
}

export async function isCircleMember(args: { circleId: string; userId: string }) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circle_members")
    .select("circle_id")
    .eq("circle_id", args.circleId)
    .eq("user_id", args.userId)
    .maybeSingle();

  if (error) throw error;
  return Boolean(data);
}

export async function listCircleMembers(circleId: string) {
  const supabase = getSupabaseClient();

  const attempt = await supabase
    .from("circle_members")
    .select("user_id, created_at, role")
    .eq("circle_id", circleId)
    .order("created_at", { ascending: true });

  let data: any[] | null = attempt.data as any[] | null;
  let error = attempt.error as any;

  // Backwards compatibility: if DB doesn't have the role column yet.
  if (error && typeof error.message === "string" && /role/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_members")
      .select("user_id, created_at")
      .eq("circle_id", circleId)
      .order("created_at", { ascending: true });
    data = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error) throw error;

  const rows = (data ?? []) as any[];
  const ids = rows.map((r) => String(r.user_id ?? "")).filter(Boolean);
  const rolesById: Record<string, CircleMemberRole> = {};

  for (const r of rows) {
    const id = String(r.user_id ?? "");
    if (!id) continue;
    rolesById[id] = r.role === "admin" ? "admin" : "member";
  }

  const profiles = await fetchProfilesByIds(ids);

  return ids
    .map((id) => {
      const p = profiles.get(id);
      return {
        id,
        displayName: p ? normalizeProfileName(p) : "Biri",
        handle: p && typeof p.handle === "string" ? p.handle : "",
        photoUrl: p && typeof p.photo_url === "string" ? p.photo_url : null,
        role: rolesById[id] ?? "member",
      } satisfies CircleMemberProfile;
    })
    .filter(Boolean);
}

export async function getCircleMemberRole(args: { circleId: string; userId: string }): Promise<CircleMemberRole | null> {
  const supabase = getSupabaseClient();
  const attempt = await supabase
    .from("circle_members")
    .select("role")
    .eq("circle_id", args.circleId)
    .eq("user_id", args.userId)
    .maybeSingle();

  // Backwards compatibility: if DB doesn't have the role column yet.
  if (attempt.error && typeof (attempt.error as any).message === "string") {
    const msg = String((attempt.error as any).message);
    if (/role/i.test(msg) && /column/i.test(msg)) return "member";
    throw attempt.error;
  }

  if (!attempt.data) return null;
  return (attempt.data as any).role === "admin" ? "admin" : "member";
}

export async function removeCircleMemberInDb(args: { circleId: string; userId: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("circle_members")
    .delete()
    .eq("circle_id", args.circleId)
    .eq("user_id", args.userId);

  if (error) throw error;
}

export async function setCircleMemberRoleInDb(args: { circleId: string; userId: string; role: CircleMemberRole }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("circle_members")
    .update({ role: args.role } as any)
    .eq("circle_id", args.circleId)
    .eq("user_id", args.userId);

  if (error && typeof (error as any).message === "string") {
    const msg = String((error as any).message);
    if (/role/i.test(msg) && /column/i.test(msg)) {
      throw new Error("Yönetici atamak için circle_members tablosuna role kolonu eklenmeli.");
    }
  }

  if (error) throw error;
}

export async function addCircleMemberByHandleInDb(args: { circleId: string; handle: string; role?: CircleMemberRole }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const handle = args.handle.trim().replace(/^@+/, "");
  if (!handle) throw new Error("Kullanıcı adı (handle) gerekli");

  const supabase = getSupabaseClient();
  const { data: profile, error: profileError } = await supabase
    .from("karma_profiles")
    .select("id")
    .eq("handle", handle)
    .maybeSingle();

  if (profileError) throw profileError;
  if (!profile?.id) throw new Error("Bu handle ile kullanıcı bulunamadı");

  const role = args.role ?? "member";

  const insertAttempt = await supabase
    .from("circle_members")
    .upsert({ circle_id: args.circleId, user_id: String(profile.id), role } as any, {
      onConflict: "circle_id,user_id",
    });

  if (insertAttempt.error && typeof (insertAttempt.error as any).message === "string") {
    const msg = String((insertAttempt.error as any).message);
    if (/role/i.test(msg) && /column/i.test(msg)) {
      if (role !== "member") {
        throw new Error("Yönetici eklemek için circle_members tablosuna role kolonu eklenmeli.");
      }

      const retry = await supabase
        .from("circle_members")
        .upsert({ circle_id: args.circleId, user_id: String(profile.id) } as any, {
          onConflict: "circle_id,user_id",
        });
      if (retry.error) throw retry.error;
      return;
    }
  }

  if (insertAttempt.error) throw insertAttempt.error;
}

export async function listCircleNumerologyMatchCandidatesInDb(args: {
  circleId: string;
  limit?: number;
  minScore?: number;
}): Promise<CircleNumerologyMatchCandidate[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const { isAdmin, isOwner } = await canManageCircle({ circleId: args.circleId, userId: sessionUser.id });
  if (!isAdmin && !isOwner) return [];

  const supabase = getSupabaseClient();
  const { data: circleRow, error: circleError } = await supabase
    .from("circles")
    .select("id, rules")
    .eq("id", args.circleId)
    .maybeSingle();

  if (circleError) throw circleError;
  if (!circleRow) return [];

  const rules = normalizeRuleSet((circleRow as any).rules);
  if (!rules?.all?.length) return [];

  const [membersResult, invitesResult] = await Promise.all([
    supabase.from("circle_members").select("user_id").eq("circle_id", args.circleId),
    supabase
      .from("circle_invites")
      .select("receiver_user_id")
      .eq("circle_id", args.circleId)
      .eq("status", "pending"),
  ]);

  if (membersResult.error) throw membersResult.error;
  if (invitesResult.error && !isMissingRelationError(invitesResult.error)) throw invitesResult.error;

  const blockedUserIds = new Set<string>([
    sessionUser.id,
    ...((membersResult.data ?? []) as any[]).map((row) => String(row.user_id ?? "")).filter(Boolean),
    ...((invitesResult.data ?? []) as any[]).map((row) => String(row.receiver_user_id ?? "")).filter(Boolean),
  ]);

  const scanLimit = Math.max(50, Math.min(600, Math.trunc(Number(args.limit) || 200) * 8));
  const minScore = Math.max(0, Math.min(100, Math.trunc(Number(args.minScore) || 50)));

  const { data: profileRows, error: profilesError } = await supabase
    .from("karma_profiles")
    .select("id, display_name, handle, photo_url, birth_date")
    .not("birth_date", "is", null)
    .not("handle", "is", null)
    .range(0, scanLimit - 1);

  if (profilesError) throw profilesError;

  const candidates: CircleNumerologyMatchCandidate[] = [];

  for (const row of (profileRows ?? []) as any[]) {
    const userId = String(row.id ?? "");
    if (!userId || blockedUserIds.has(userId)) continue;

    const handle = String(row.handle ?? "").trim().replace(/^@+/, "");
    if (!handle) continue;

    const displayName = normalizeProfileName(row);
    const birthDate = parseBirthDateFromIso(row.birth_date);
    if (!displayName || !birthDate) continue;

    const scorePack = computeCircleRuleMatchScore({ fullName: displayName, birthDate, rules });
    if (scorePack.score < minScore) continue;

    candidates.push({
      userId,
      displayName,
      handle,
      photoUrl: typeof row.photo_url === "string" ? row.photo_url : null,
      matchScore: scorePack.score,
      matchedRules: scorePack.matched,
      totalRules: scorePack.total,
    });
  }

  const outLimit = Math.max(1, Math.min(40, Math.trunc(Number(args.limit) || 12)));
  return candidates
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
      if (b.matchedRules !== a.matchedRules) return b.matchedRules - a.matchedRules;
      return a.displayName.localeCompare(b.displayName, "tr-TR");
    })
    .slice(0, outLimit);
}

export async function sendBulkCircleInvitesInDb(args: {
  circleId: string;
  invites: Array<{ handle: string; matchScore?: number | null }>;
  message?: string;
  inviteType?: "manual" | "numerology_match";
}) {
  const uniqueInvites = Array.from(
    new Map(
      (args.invites ?? [])
        .map((invite) => {
          const handle = String(invite.handle ?? "").trim().replace(/^@+/, "");
          if (!handle) return null;
          return [handle.toLocaleLowerCase("tr-TR"), { ...invite, handle }] as const;
        })
        .filter(Boolean) as Array<readonly [string, { handle: string; matchScore?: number | null }]>,
    ).values(),
  ).slice(0, 60);

  if (!uniqueInvites.length) {
    return { sentCount: 0, fallbackMemberAddCount: 0, skippedCount: 0, errorCount: 0, errors: [] as Array<{ handle: string; reason: string }> };
  }

  let sentCount = 0;
  let fallbackMemberAddCount = 0;
  let skippedCount = 0;
  const errors: Array<{ handle: string; reason: string }> = [];

  for (const invite of uniqueInvites) {
    try {
      const result = await sendCircleInviteByHandleInDb({
        circleId: args.circleId,
        handle: invite.handle,
        inviteType: args.inviteType ?? "numerology_match",
        matchScore: invite.matchScore,
        message: args.message,
      });
      sentCount += 1;
      if (result.fallbackMemberAdd) fallbackMemberAddCount += 1;
    } catch (error) {
      const code = String((error as any)?.code ?? "");
      const reason = error instanceof Error ? error.message : "Davet gönderilemedi";
      if (code === "23505" || reason.toLocaleLowerCase("tr-TR").includes("duplicate")) {
        skippedCount += 1;
        continue;
      }
      errors.push({ handle: invite.handle, reason });
    }
  }

  return { sentCount, fallbackMemberAddCount, skippedCount, errorCount: errors.length, errors };
}

export async function sendCircleInviteByHandleInDb(args: {
  circleId: string;
  handle: string;
  inviteType?: "manual" | "numerology_match";
  matchScore?: number | null;
  message?: string;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const handle = String(args.handle ?? "").trim().replace(/^@+/, "");
  if (!handle) throw new Error("Kullanıcı adı gerekli");

  const supabase = getSupabaseClient();
  const { data: profile, error: profileError } = await supabase
    .from("karma_profiles")
    .select("id")
    .eq("handle", handle)
    .maybeSingle();

  if (profileError) throw profileError;
  if (!profile?.id) throw new Error("Bu kullanıcı bulunamadı");

  const matchScoreRaw = Number(args.matchScore);
  const matchScore = Number.isFinite(matchScoreRaw) ? Math.max(0, Math.min(100, Math.trunc(matchScoreRaw))) : null;

  const { error } = await supabase.from("circle_invites").insert({
    circle_id: args.circleId,
    sender_user_id: sessionUser.id,
    receiver_user_id: String(profile.id),
    invite_type: args.inviteType ?? "manual",
    match_score: matchScore,
    message: args.message ? String(args.message).trim().slice(0, 280) : null,
    status: "pending",
  } as any);

  if (error && isMissingRelationError(error)) {
    await addCircleMemberByHandleInDb({ circleId: args.circleId, handle, role: "member" });
    return { fallbackMemberAdd: true };
  }

  if (error) throw error;
  return { fallbackMemberAdd: false };
}

export async function listMyCircleInvitesInDb(): Promise<CircleInvite[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circle_invites")
    .select("id, circle_id, sender_user_id, receiver_user_id, invite_type, match_score, message, status, created_at, responded_at, circles(name)")
    .eq("receiver_user_id", sessionUser.id)
    .order("created_at", { ascending: false })
    .limit(60);

  if (error && isMissingRelationError(error)) {
    return [];
  }
  if (error) throw error;

  const rows = (data ?? []) as any[];
  const senderIds = rows.map((row) => String(row.sender_user_id ?? "")).filter(Boolean);
  const senderProfiles = await fetchProfilesByIds(senderIds);

  return rows.map((row) => {
    const senderId = String(row.sender_user_id ?? "");
    const senderProfile = senderProfiles.get(senderId);
    return {
      id: String(row.id ?? ""),
      circleId: String(row.circle_id ?? ""),
      circleName: String((row as any)?.circles?.name ?? "Çember"),
      senderUserId: senderId,
      senderName: senderProfile ? normalizeProfileName(senderProfile) : "Bir üye",
      receiverUserId: String(row.receiver_user_id ?? ""),
      inviteType: row.invite_type === "numerology_match" ? "numerology_match" : "manual",
      matchScore: Number.isFinite(Number(row.match_score)) ? Math.trunc(Number(row.match_score)) : null,
      message: typeof row.message === "string" ? row.message : null,
      status: row.status === "accepted" || row.status === "declined" || row.status === "expired" ? row.status : "pending",
      createdAt: String(row.created_at ?? ""),
      respondedAt: typeof row.responded_at === "string" ? row.responded_at : null,
    } satisfies CircleInvite;
  });
}

export async function respondToCircleInviteInDb(args: { inviteId: string; action: "accept" | "decline" }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const nextStatus = args.action === "accept" ? "accepted" : "declined";

  const { data, error } = await supabase
    .from("circle_invites")
    .update({ status: nextStatus, responded_at: new Date().toISOString() } as any)
    .eq("id", args.inviteId)
    .eq("receiver_user_id", sessionUser.id)
    .eq("status", "pending")
    .select("id, circle_id")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("Davet bulunamadı veya zaten işlenmiş");

  if (args.action === "accept") {
    const { error: joinError } = await supabase
      .from("circle_members")
      .upsert({ circle_id: String((data as any).circle_id), user_id: sessionUser.id } as any, {
        onConflict: "circle_id,user_id",
        ignoreDuplicates: true,
      });
    if (joinError) throw joinError;
  }
}

function isMissingTableOrColumn(err: any, needle: string) {
  const msg = typeof err?.message === "string" ? err.message : "";
  return msg.toLocaleLowerCase("tr-TR").includes(needle.toLocaleLowerCase("tr-TR"));
}

function getLocalDateKey(d = new Date()) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function canManageCircle(args: { circleId: string; userId: string }) {
  const supabase = getSupabaseClient();
  const { data: circle, error: circleError } = await supabase
    .from("circles")
    .select("owner_id")
    .eq("id", args.circleId)
    .maybeSingle();

  if (circleError) throw circleError;

  const ownerId = circle && (circle as any).owner_id ? String((circle as any).owner_id) : "";
  if (ownerId && ownerId === args.userId) return { isOwner: true, isAdmin: true };

  // Admin role is optional (requires circle_members.role column).
  const role = await getCircleMemberRole({ circleId: args.circleId, userId: args.userId }).catch((e) => {
    if (isMissingTableOrColumn(e, "role") && isMissingTableOrColumn(e, "column")) return "member" as const;
    throw e;
  });

  return { isOwner: false, isAdmin: role === "admin" };
}

export async function listCircleTasks(circleId: string): Promise<CircleTask[]> {
  const supabase = getSupabaseClient();

  const attempt = await supabase
    .from("circle_tasks")
    .select("id, circle_id, title, description, kind, points, sort_order, active, created_at, created_by")
    .eq("circle_id", circleId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });

  let data: any[] | null = attempt.data as any[] | null;
  let error: any = attempt.error as any;

  // Backwards compatibility: old schema
  if (error && typeof error.message === "string" && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_tasks")
      .select("id, circle_id, title, created_at, created_by")
      .eq("circle_id", circleId)
      .order("created_at", { ascending: true });
    data = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error) {
    if (isMissingTableOrColumn(error, "circle_tasks")) {
      throw new Error("Görevler için circle_tasks tablosu eklenmeli.");
    }
    throw error;
  }

  const rows = (data ?? []) as any[];
  return rows.map((r, idx) => {
    const kindRaw = typeof r.kind === "string" ? r.kind : "ozel";
    const kind: CircleTaskKind =
      kindRaw === "meditasyon" || kindRaw === "nefes" || kindRaw === "diyet" || kindRaw === "rituel" || kindRaw === "sohbet" ? kindRaw : "ozel";

    const points = Number.isFinite(Number(r.points)) ? Math.max(0, Math.min(100, Math.trunc(Number(r.points)))) : 1;
    const sortOrder = Number.isFinite(Number(r.sort_order)) ? Math.trunc(Number(r.sort_order)) : idx;

    return {
      id: String(r.id),
      circleId: String(r.circle_id),
      title: String(r.title ?? ""),
      description: typeof r.description === "string" && r.description.trim() ? r.description.trim() : null,
      kind,
      points,
      sortOrder,
      active: r.active === false ? false : true,
      createdAt: r.created_at ? String(r.created_at) : null,
      createdBy: r.created_by ? String(r.created_by) : null,
    } satisfies CircleTask;
  });
}

export async function listCircleTasksForMe(
  circleId: string,
  dateKey = getLocalDateKey(),
  includeInactive = false,
): Promise<CircleTaskWithStatus[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const baseTasks = await listCircleTasks(circleId);
  const tasks = includeInactive ? baseTasks : baseTasks.filter((t) => t.active);
  if (!tasks.length) return [];

  const supabase = getSupabaseClient();
  const ids = tasks.map((t) => t.id);

  // Prefer daily completion rows (date_key). If column doesn't exist, fall back to legacy behavior.
  const attempt = await supabase
    .from("circle_task_completions")
    .select("task_id")
    .eq("circle_id", circleId)
    .eq("user_id", sessionUser.id)
    .eq("date_key", dateKey)
    .in("task_id", ids);

  let data: any[] | null = attempt.data as any[] | null;
  let error: any = attempt.error as any;

  if (error && typeof error.message === "string" && /date_key/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_task_completions")
      .select("task_id")
      .eq("circle_id", circleId)
      .eq("user_id", sessionUser.id)
      .in("task_id", ids);
    data = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error) {
    if (isMissingTableOrColumn(error, "circle_task_completions")) {
      throw new Error("Görev tamamlamaları için circle_task_completions tablosu eklenmeli.");
    }
    throw error;
  }

  const done = new Set(((data ?? []) as any[]).map((r) => String(r.task_id ?? "")).filter(Boolean));
  return tasks.map((t) => ({ ...t, completed: done.has(t.id), dateKey }));
}

export async function toggleCircleTaskCompletionInDb(args: { circleId: string; taskId: string; dateKey?: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const dateKey = args.dateKey ?? getLocalDateKey();

  const attempt = await supabase
    .from("circle_task_completions")
    .select("id")
    .eq("circle_id", args.circleId)
    .eq("task_id", args.taskId)
    .eq("user_id", sessionUser.id)
    .eq("date_key", dateKey)
    .maybeSingle();

  let existingId: string | null = attempt.data?.id ? String((attempt.data as any).id) : null;
  let error: any = attempt.error as any;

  // Legacy schema without date_key
  if (error && typeof error.message === "string" && /date_key/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_task_completions")
      .select("id")
      .eq("circle_id", args.circleId)
      .eq("task_id", args.taskId)
      .eq("user_id", sessionUser.id)
      .maybeSingle();

    existingId = retry.data?.id ? String((retry.data as any).id) : null;
    error = retry.error as any;
  }

  if (error) {
    if (isMissingTableOrColumn(error, "circle_task_completions")) {
      throw new Error("Görev tamamlamaları için circle_task_completions tablosu eklenmeli.");
    }
    throw error;
  }

  if (existingId) {
    const del = await supabase.from("circle_task_completions").delete().eq("id", existingId);
    if (del.error) throw del.error;

    const memberTaskPatch = await supabase
      .from("circle_member_tasks")
      .upsert(
        {
          circle_id: args.circleId,
          task_id: args.taskId,
          user_id: sessionUser.id,
          status: "pending",
          completed_at: null,
        } as any,
        { onConflict: "circle_id,task_id,user_id" },
      );

    if (memberTaskPatch.error && !isMissingRelationError(memberTaskPatch.error)) throw memberTaskPatch.error;
    return { completed: false };
  }

  const insertAttempt = await supabase
    .from("circle_task_completions")
    .insert({ circle_id: args.circleId, task_id: args.taskId, user_id: sessionUser.id, date_key: dateKey } as any);

  if (insertAttempt.error && typeof insertAttempt.error.message === "string" && /date_key/i.test(insertAttempt.error.message) && /column/i.test(insertAttempt.error.message)) {
    const retry = await supabase
      .from("circle_task_completions")
      .insert({ circle_id: args.circleId, task_id: args.taskId, user_id: sessionUser.id } as any);
    if (retry.error) throw retry.error;
    return { completed: true };
  }

  if (insertAttempt.error) throw insertAttempt.error;

  const memberTaskPatch = await supabase
    .from("circle_member_tasks")
    .upsert(
      {
        circle_id: args.circleId,
        task_id: args.taskId,
        user_id: sessionUser.id,
        status: "completed",
        completed_at: new Date().toISOString(),
      } as any,
      { onConflict: "circle_id,task_id,user_id" },
    );

  if (memberTaskPatch.error && !isMissingRelationError(memberTaskPatch.error)) throw memberTaskPatch.error;
  return { completed: true };
}

export async function listMyTaskCompletionsByDateKeys(dateKeys: string[]): Promise<Record<string, number>> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const keys = Array.from(new Set((dateKeys ?? []).map((k) => String(k || "").trim()).filter(Boolean)));
  if (!keys.length) return {};

  const supabase = getSupabaseClient();

  const memberTaskAttempt = await supabase
    .from("circle_member_tasks")
    .select("completed_at")
    .eq("user_id", sessionUser.id)
    .eq("status", "completed");

  if (!memberTaskAttempt.error) {
    const out: Record<string, number> = {};
    for (const row of (memberTaskAttempt.data ?? []) as any[]) {
      const completedAt = typeof row?.completed_at === "string" ? row.completed_at : "";
      if (!completedAt) continue;
      const key = completedAt.slice(0, 10);
      if (!keys.includes(key)) continue;
      out[key] = (out[key] ?? 0) + 1;
    }
    return out;
  }

  if (memberTaskAttempt.error && !isMissingRelationError(memberTaskAttempt.error)) {
    throw memberTaskAttempt.error;
  }

  const attempt = await supabase
    .from("circle_task_completions")
    .select("date_key")
    .eq("user_id", sessionUser.id)
    .in("date_key", keys);

  if (attempt.error) {
    // Legacy schema without date_key or missing table.
    if (isMissingTableOrColumn(attempt.error, "circle_task_completions") || (isMissingTableOrColumn(attempt.error, "date_key") && isMissingTableOrColumn(attempt.error, "column"))) {
      return {};
    }
    throw attempt.error;
  }

  const out: Record<string, number> = {};
  for (const r of (attempt.data ?? []) as any[]) {
    const k = typeof r?.date_key === "string" ? r.date_key : "";
    if (!k) continue;
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

export type CircleTaskActivityEvent = {
  id: string;
  userId: string;
  userDisplayName: string;
  taskId: string;
  taskTitle: string;
  kind: CircleTaskKind;
  dateKey: string;
  createdAt: string | null;
};

export async function listCircleTaskActivityFeed(args: { circleId: string; limit?: number }): Promise<CircleTaskActivityEvent[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const limit = Number.isFinite(Number(args.limit)) ? Math.max(1, Math.min(50, Math.trunc(Number(args.limit)))) : 12;
  const supabase = getSupabaseClient();

  const tasks = await listCircleTasks(args.circleId).catch(() => [] as CircleTask[]);
  const taskById = new Map<string, CircleTask>(tasks.map((t) => [t.id, t] as const));

  const members = await listCircleMembers(args.circleId).catch(() => [] as CircleMemberProfile[]);
  const memberNameById = new Map<string, string>(members.map((m) => [m.id, m.displayName] as const));

  const tryWithCreatedAt = await supabase
    .from("circle_task_completions")
    .select("id, user_id, task_id, date_key, created_at")
    .eq("circle_id", args.circleId)
    .order("created_at", { ascending: false })
    .limit(limit * 3);

  let rows: any[] | null = tryWithCreatedAt.data as any[] | null;
  let error: any = tryWithCreatedAt.error as any;

  if (error && typeof error.message === "string" && /created_at/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_task_completions")
      .select("id, user_id, task_id, date_key")
      .eq("circle_id", args.circleId)
      .order("date_key", { ascending: false })
      .limit(limit * 3);
    rows = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error && typeof error.message === "string" && /date_key/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase
      .from("circle_task_completions")
      .select("id, user_id, task_id, created_at")
      .eq("circle_id", args.circleId)
      .order("created_at", { ascending: false })
      .limit(limit * 3);
    rows = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error && typeof error.message === "string" && /created_at/i.test(error.message) && /column/i.test(error.message)) {
    const retry = await supabase.from("circle_task_completions").select("id, user_id, task_id").eq("circle_id", args.circleId).limit(limit * 3);
    rows = retry.data as any[] | null;
    error = retry.error as any;
  }

  if (error) {
    if (isMissingTableOrColumn(error, "circle_task_completions")) {
      return [];
    }
    throw error;
  }

  const todayKey = getLocalDateKey();
  const mapped = ((rows ?? []) as any[])
    .map((r) => {
      const taskId = String(r?.task_id ?? "");
      if (!taskId) return null;

      const task = taskById.get(taskId);
      const userId = String(r?.user_id ?? "");
      const displayName = memberNameById.get(userId) ?? "Bir üye";
      const dateKey = typeof r?.date_key === "string" && r.date_key ? r.date_key : todayKey;
      const createdAt = typeof r?.created_at === "string" && r.created_at ? r.created_at : null;

      return {
        id: String(r?.id ?? `${taskId}:${userId}:${dateKey}`),
        userId,
        userDisplayName: displayName,
        taskId,
        taskTitle: task?.title ?? "Görev",
        kind: task?.kind ?? "ozel",
        dateKey,
        createdAt,
      } satisfies CircleTaskActivityEvent;
    })
    .filter(Boolean) as CircleTaskActivityEvent[];

  mapped.sort((a, b) => {
    const ta = a.createdAt ? Date.parse(a.createdAt) : Date.parse(`${a.dateKey}T00:00:00`);
    const tb = b.createdAt ? Date.parse(b.createdAt) : Date.parse(`${b.dateKey}T00:00:00`);
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });

  return mapped.slice(0, limit);
}

export async function createCircleTaskInDb(args: {
  circleId: string;
  title: string;
  description?: string | null;
  kind?: CircleTaskKind;
  points?: number;
  sortOrder?: number;
  active?: boolean;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const title = args.title.trim().slice(0, 80);
  if (!title) throw new Error("Görev başlığı gerekli");

  const perms = await canManageCircle({ circleId: args.circleId, userId: sessionUser.id });
  if (!perms.isAdmin) {
    throw new Error("Görev eklemek için çember yöneticisi olmalısın.");
  }

  const description = typeof args.description === "string" ? args.description.trim().slice(0, 280) : "";
  const kind: CircleTaskKind = args.kind ?? "ozel";
  const points = Number.isFinite(Number(args.points)) ? Math.max(0, Math.min(100, Math.trunc(Number(args.points)))) : 1;
  const sortOrder = Number.isFinite(Number(args.sortOrder)) ? Math.trunc(Number(args.sortOrder)) : 0;
  const active = args.active === false ? false : true;

  const supabase = getSupabaseClient();

  const attempt = await supabase.from("circle_tasks").insert({
    circle_id: args.circleId,
    title,
    description: description || null,
    kind,
    points,
    sort_order: sortOrder,
    active,
    created_by: sessionUser.id,
  } as any);

  if (attempt.error && typeof attempt.error.message === "string" && /column/i.test(attempt.error.message)) {
    // Legacy schema: title only
    const retry = await supabase.from("circle_tasks").insert({ circle_id: args.circleId, title, created_by: sessionUser.id } as any);
    if (retry.error) {
      if (isMissingTableOrColumn(retry.error, "circle_tasks")) throw new Error("Görevler için circle_tasks tablosu eklenmeli.");
      throw retry.error;
    }
    return;
  }

  if (attempt.error) {
    if (isMissingTableOrColumn(attempt.error, "circle_tasks")) {
      throw new Error("Görevler için circle_tasks tablosu eklenmeli.");
    }
    throw attempt.error;
  }
}

export async function updateCircleTaskInDb(args: {
  circleId: string;
  taskId: string;
  title?: string;
  description?: string | null;
  kind?: CircleTaskKind;
  points?: number;
  sortOrder?: number;
  active?: boolean;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const perms = await canManageCircle({ circleId: args.circleId, userId: sessionUser.id });
  if (!perms.isAdmin) {
    throw new Error("Görev düzenlemek için çember yöneticisi olmalısın.");
  }

  const patch: Record<string, any> = {};
  if (typeof args.title === "string") {
    const title = args.title.trim().slice(0, 80);
    if (!title) throw new Error("Görev başlığı gerekli");
    patch.title = title;
  }
  if (args.description !== undefined) {
    patch.description = typeof args.description === "string" ? args.description.trim().slice(0, 280) || null : null;
  }
  if (args.kind) patch.kind = args.kind;
  if (args.points !== undefined) {
    patch.points = Number.isFinite(Number(args.points)) ? Math.max(0, Math.min(100, Math.trunc(Number(args.points)))) : 1;
  }
  if (args.sortOrder !== undefined) {
    patch.sort_order = Number.isFinite(Number(args.sortOrder)) ? Math.trunc(Number(args.sortOrder)) : 0;
  }
  if (args.active !== undefined) patch.active = Boolean(args.active);

  const supabase = getSupabaseClient();
  const attempt = await supabase.from("circle_tasks").update(patch).eq("id", args.taskId).eq("circle_id", args.circleId);

  if (attempt.error && typeof attempt.error.message === "string" && /column/i.test(attempt.error.message)) {
    throw new Error("Görev düzenleme için circle_tasks tablosuna (description, kind, points, sort_order, active) kolonları eklenmeli.");
  }

  if (attempt.error) throw attempt.error;
}

export async function deleteCircleTaskInDb(args: { circleId: string; taskId: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const perms = await canManageCircle({ circleId: args.circleId, userId: sessionUser.id });
  if (!perms.isAdmin) {
    throw new Error("Görev silmek için çember yöneticisi olmalısın.");
  }

  const supabase = getSupabaseClient();

  const delComps = await supabase.from("circle_task_completions").delete().eq("circle_id", args.circleId).eq("task_id", args.taskId);
  if (delComps.error && !isMissingTableOrColumn(delComps.error, "circle_task_completions")) throw delComps.error;

  const delTask = await supabase.from("circle_tasks").delete().eq("id", args.taskId).eq("circle_id", args.circleId);
  if (delTask.error) throw delTask.error;
}

export async function fetchCircleTaskCompletionCounts(circleId: string): Promise<Record<string, number>> {
  const supabase = getSupabaseClient();

  const memberTaskAttempt = await supabase
    .from("circle_member_tasks")
    .select("user_id")
    .eq("circle_id", circleId)
    .eq("status", "completed");

  if (!memberTaskAttempt.error) {
    const out: Record<string, number> = {};
    for (const row of (memberTaskAttempt.data ?? []) as any[]) {
      const id = String(row.user_id ?? "");
      if (!id) continue;
      out[id] = (out[id] ?? 0) + 1;
    }
    return out;
  }

  if (memberTaskAttempt.error && !isMissingRelationError(memberTaskAttempt.error)) {
    throw memberTaskAttempt.error;
  }

  const { data, error } = await supabase
    .from("circle_task_completions")
    .select("user_id")
    .eq("circle_id", circleId);

  if (error) {
    if (isMissingTableOrColumn(error, "circle_task_completions")) {
      return {};
    }
    throw error;
  }

  const out: Record<string, number> = {};
  for (const row of (data ?? []) as any[]) {
    const id = String(row.user_id ?? "");
    if (!id) continue;
    out[id] = (out[id] ?? 0) + 1;
  }
  return out;
}

export async function fetchCircleEnergySnapshotInDb(circleId: string) {
  const supabase = getSupabaseClient();
  const todayKey = getLocalDateKey();

  const statsAttempt = await supabase
    .from("circle_daily_stats")
    .select("energy_score, engagement_score, active_members, total_members, completed_tasks_count")
    .eq("circle_id", circleId)
    .eq("stat_date", todayKey)
    .maybeSingle();

  if (!statsAttempt.error && statsAttempt.data) {
    const row = statsAttempt.data as any;
    return {
      energyScore: Number.isFinite(Number(row.energy_score)) ? Math.trunc(Number(row.energy_score)) : 0,
      engagementScore: Number.isFinite(Number(row.engagement_score)) ? Math.trunc(Number(row.engagement_score)) : 0,
      activeMembers: Number.isFinite(Number(row.active_members)) ? Math.trunc(Number(row.active_members)) : 0,
      totalMembers: Number.isFinite(Number(row.total_members)) ? Math.trunc(Number(row.total_members)) : 0,
      completedTasksCount: Number.isFinite(Number(row.completed_tasks_count)) ? Math.trunc(Number(row.completed_tasks_count)) : 0,
    };
  }

  if (statsAttempt.error && !isMissingRelationError(statsAttempt.error)) {
    throw statsAttempt.error;
  }

  const membersAttempt = await supabase.from("circle_members").select("user_id").eq("circle_id", circleId);
  if (membersAttempt.error) throw membersAttempt.error;

  const memberIds = Array.from(new Set(((membersAttempt.data ?? []) as any[]).map((row) => String(row.user_id ?? "")).filter(Boolean)));
  const totalMembers = memberIds.length;

  const memberTaskAttempt = await supabase
    .from("circle_member_tasks")
    .select("user_id")
    .eq("circle_id", circleId)
    .eq("status", "completed")
    .gte("completed_at", `${todayKey}T00:00:00`)
    .lt("completed_at", `${todayKey}T23:59:59.999`);

  let activeMembers = 0;
  let completedTasksCount = 0;

  if (!memberTaskAttempt.error) {
    const rows = (memberTaskAttempt.data ?? []) as any[];
    completedTasksCount = rows.length;
    activeMembers = new Set(rows.map((row) => String(row.user_id ?? "")).filter(Boolean)).size;
  } else if (!isMissingRelationError(memberTaskAttempt.error)) {
    throw memberTaskAttempt.error;
  } else {
    const fallback = await supabase.from("circle_task_completions").select("user_id").eq("circle_id", circleId).eq("date_key", todayKey);
    if (!fallback.error) {
      const rows = (fallback.data ?? []) as any[];
      completedTasksCount = rows.length;
      activeMembers = new Set(rows.map((row) => String(row.user_id ?? "")).filter(Boolean)).size;
    }
  }

  const energyScore = totalMembers > 0 ? Math.max(0, Math.min(100, Math.round((activeMembers / totalMembers) * 100))) : 0;
  const engagementBase = totalMembers > 0 ? completedTasksCount / Math.max(1, totalMembers) : 0;
  const engagementScore = Math.max(0, Math.min(100, Math.round(engagementBase * 35)));

  const upsert = await supabase.from("circle_daily_stats").upsert(
    {
      circle_id: circleId,
      stat_date: todayKey,
      total_members: totalMembers,
      active_members: activeMembers,
      completed_tasks_count: completedTasksCount,
      energy_score: energyScore,
      engagement_score: engagementScore,
    } as any,
    { onConflict: "circle_id,stat_date" },
  );

  if (upsert.error && !isMissingRelationError(upsert.error)) throw upsert.error;

  return { energyScore, engagementScore, activeMembers, totalMembers, completedTasksCount };
}

export async function updateCircleInDb(args: { circleId: string; name?: string; activity?: string | null; description?: string | null }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const perms = await canManageCircle({ circleId: args.circleId, userId: sessionUser.id });
  if (!perms.isAdmin) {
    throw new Error("Çember ayarlarını güncellemek için yönetici olmalısın.");
  }

  const supabase = getSupabaseClient();
  const patch: Record<string, any> = {};

  if ("name" in args) {
    const name = String(args.name ?? "").trim();
    if (name.length < 2) throw new Error("Çember adı en az 2 karakter olmalı.");
    patch.name = name.slice(0, 120);
  }

  if ("activity" in args) patch.activity = args.activity ? String(args.activity).trim().slice(0, 280) : null;
  if ("description" in args) patch.description = args.description ? String(args.description).trim().slice(0, 280) : null;

  if (Object.keys(patch).length === 0) return;

  const { error } = await supabase.from("circles").update(patch).eq("id", args.circleId);
  if (error) throw error;
}

export async function deleteCircleInDb(circleId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();

  const { data: circle, error: circleError } = await supabase
    .from("circles")
    .select("id, owner_id")
    .eq("id", circleId)
    .maybeSingle();

  if (circleError) throw circleError;
  if (!circle) throw new Error("Çember bulunamadı");

  const ownerId = (circle as any).owner_id ? String((circle as any).owner_id) : null;
  if (!ownerId || ownerId !== sessionUser.id) {
    throw new Error("Sadece çemberi oluşturan kişi çemberi silebilir.");
  }

  // Delete children first in case FK cascade is not enabled.
  const msgDel = await supabase.from("circle_messages").delete().eq("circle_id", circleId);
  if (msgDel.error) throw msgDel.error;

  const memDel = await supabase.from("circle_members").delete().eq("circle_id", circleId);
  if (memDel.error) throw memDel.error;

  const circleDel = await supabase.from("circles").delete().eq("id", circleId).eq("owner_id", sessionUser.id);
  if (circleDel.error) throw circleDel.error;
}

// Used by chat panel: ensures my profile + membership exists.
export async function ensureCircleAndMembership(args: { circleId: string; me: { displayName: string; handle: string } }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  await upsertMyProfile({ displayName: args.me.displayName, handle: args.me.handle });

  const supabase = getSupabaseClient();
  const { error: memberError } = await supabase
    .from("circle_members")
    .upsert({ circle_id: args.circleId, user_id: sessionUser.id } as any, {
      onConflict: "circle_id,user_id",
      ignoreDuplicates: true,
    });

  if (memberError) throw memberError;
}

export async function listCircleMessages(args: { circleId: string; limit?: number }) {
  const supabase = getSupabaseClient();
  const limit = Math.max(1, Math.min(200, Math.trunc(args.limit ?? 60)));

  const { data, error } = await supabase
    .from("circle_messages")
    .select("id, circle_id, user_id, body, created_at")
    .eq("circle_id", args.circleId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;

  const rows = (data ?? []) as any[];
  const userIds = rows.map((r) => String(r.user_id ?? "")).filter(Boolean);
  const profiles = await fetchProfilesByIds(userIds);

  return rows
    .map((r) => {
      const userId = String(r.user_id ?? "");
      const profile = profiles.get(userId);
      return {
        id: String(r.id),
        circleId: String(r.circle_id),
        userId,
        body: String(r.body ?? ""),
        createdAt: String(r.created_at),
        senderName: profile ? normalizeProfileName(profile) : "Biri",
      } satisfies CircleMessage;
    })
    .reverse();
}

export async function sendCircleMessage(args: { circleId: string; body: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const body = args.body.trim();
  if (!body) return;

  const supabase = getSupabaseClient();
  const { error } = await supabase.from("circle_messages").insert({
    circle_id: args.circleId,
    user_id: sessionUser.id,
    body,
  });

  if (error) throw error;
}

export function subscribeCircleMessages(args: { circleId: string; onInserted: (messageId: string) => void }) {
  const supabase = getSupabaseClient();

  const knownIds = new Set<string>();
  let disposed = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let pollStartTimer: ReturnType<typeof setTimeout> | null = null;

  const stopPolling = () => {
    if (pollStartTimer) {
      clearTimeout(pollStartTimer);
      pollStartTimer = null;
    }
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const syncRecentMessages = async () => {
    if (disposed) return;
    try {
      const recent = await listCircleMessages({ circleId: args.circleId, limit: 40 });
      for (const msg of recent) {
        if (knownIds.has(msg.id)) continue;
        knownIds.add(msg.id);
        args.onInserted(msg.id);
      }
    } catch {
      // ignore polling failures
    }
  };

  void (async () => {
    try {
      const seed = await listCircleMessages({ circleId: args.circleId, limit: 80 });
      seed.forEach((msg) => knownIds.add(msg.id));
    } catch {
      // ignore seed failures
    }

    if (disposed) return;
    pollStartTimer = setTimeout(() => {
      if (disposed || pollTimer) return;
      pollTimer = setInterval(() => {
        void syncRecentMessages();
      }, 2500);
    }, 4000);
  })();

  const channel = supabase
    .channel(`circle:${args.circleId}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "circle_messages", filter: `circle_id=eq.${args.circleId}` },
      (payload) => {
        const id = (payload.new as any)?.id;
        if (!id) return;
        knownIds.add(String(id));
        stopPolling();
        args.onInserted(String(id));
      },
    )
    .subscribe();

  return () => {
    disposed = true;
    stopPolling();
    void supabase.removeChannel(channel);
  };
}

export async function fetchCircleMessageById(messageId: string) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("circle_messages")
    .select("id, circle_id, user_id, body, created_at")
    .eq("id", messageId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const userId = String((data as any).user_id ?? "");
  const profiles = await fetchProfilesByIds([userId]);
  const profile = profiles.get(userId);

  const r: any = data;
  return {
    id: String(r.id),
    circleId: String(r.circle_id),
    userId,
    body: String(r.body ?? ""),
    createdAt: String(r.created_at),
    senderName: profile ? normalizeProfileName(profile) : "Biri",
  } satisfies CircleMessage;
}
